import { useMemo, useState, useEffect } from "react";

/**
 * Persist a small piece of UI state (sort choice, view mode) to localStorage
 * so a table reopens the way the user left it. Pass a null key to disable.
 */
export function usePersistedState(key, initial) {
  const [val, setVal] = useState(() => {
    if (!key) return initial;
    try {
      const s = localStorage.getItem(key);
      return s !== null ? JSON.parse(s) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(val));
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [key, val]);
  return [val, setVal];
}

/**
 * Small reusable client-side sort helper used across dashboard tables.
 *
 * Usage:
 *   const sort = useSort("revenue", "desc", {
 *     units: (r) => r.units,
 *     revenue: (r) => r.revenue,
 *   }, ["design"], "top-products");
 *   const sortedRows = sort.apply(rows);
 *   <SortHeader label="Units" k="units" state={sort} />
 *
 * Null / undefined values always sink to the bottom regardless of direction.
 * Pass `persistKey` to remember the last sort across reloads.
 */
export function useSort(defaultKey, defaultDir = "desc", valGetters = {}, ascFirstKeys = [], persistKey = null) {
  const [state, setState] = usePersistedState(
    persistKey ? `bm-sort:${persistKey}` : null,
    { key: defaultKey, dir: defaultDir },
  );
  const sortKey = state.key;
  const sortDir = state.dir;

  const toggle = (k) => {
    setState((s) =>
      k === s.key
        ? { key: k, dir: s.dir === "desc" ? "asc" : "desc" }
        : { key: k, dir: ascFirstKeys.includes(k) ? "asc" : "desc" },
    );
  };

  const apply = (rows) => {
    if (!rows || !rows.length) return rows || [];
    const get = valGetters[sortKey];
    if (!get) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      const aN = va === null || va === undefined || va === "";
      const bN = vb === null || vb === undefined || vb === "";
      if (aN && bN) return 0;
      if (aN) return 1;
      if (bN) return -1;
      if (typeof va === "string" || typeof vb === "string") {
        return String(va).localeCompare(String(vb)) * dir;
      }
      return (va - vb) * dir;
    });
    return arr;
  };

  const set = (key, dir) => setState({ key, dir });

  // Return an object usable both as a value bag and via memoized `apply`.
  return useMemo(
    () => ({ sortKey, sortDir, toggle, set, apply }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortKey, sortDir],
  );
}
